<!-- 阅读页 -->
<script setup lang="ts">
import { useMessage } from 'naive-ui'
import { asyncGetBookArticle, asyncGetBookArticlePreview, asyncGetBookArticles, asyncGetBookInfo } from '@/api/book'
import type { IBookArticle, IBookArticles, IBookInfo } from '@/types/book'
import { asyncAddBookShelf, asyncAddHistory } from '@/api/book/bookshelf'
import { asyncGetUserBalance } from '@/api/user'
import { APP_STORE_LINK, GOOGLE_STORE_LINK } from '@/utils/const'

const { t } = useI18n()
const message = useMessage()
const userStore = useUserStore()
const appStore = useAppStore()
const bookStore = useBookStore()
const router = useRouter()
const route = useRoute()
const localLang = ref(process.env.LANG || 'en')

const chapterId = ref(`${route.query.chapter || 0}`) // 章节id

const isPreview = ref(false) // 是否是预览章节
const showToAppModal = ref(false) // 是否显示跳转APP弹窗
const article = ref<IBookArticle>({
  price: 0,
})

// 书籍详情
const bookInfo = ref<IBookInfo>({
  id: 0,
  avatar: '',
  discount_promotion: {
    discount: 0,
    start_time_ts: 0,
    end_time_ts: 0,
  },
  is_read_pass: false, // 是否为畅读书籍（畅读书籍不可解锁章节，其余书籍可解锁章节数仍保持2章）
})

// 添加浏览记录
const addHistory = async () => {
  await asyncAddHistory({
    novel_id: bookInfo.value.id,
  })
}

// 用户余额
const wallet = ref(0)
// 获取用户余额
const getUserBalance = async () => {
  const { data } = await asyncGetUserBalance()
  wallet.value = data.wealth + data.wealth_coupon
}

const showDirectory = ref(false) // 是否显示目录

// 获取目录
const articles = ref<IBookArticles[]>([])
const drawerLoading = ref(false)
const paySection = ref(0) // 支付章节
const getArticles = async (noLoad = false) => {
  if (!bookInfo.value.id) return
  drawerLoading.value = true
  const { data } = await asyncGetBookArticles(bookInfo.value.id, noLoad)
  drawerLoading.value = false
  if (!data && data.length === 0) {
    return
  }
  paySection.value = data.findIndex((v: IBookArticles) => v.price > 0)
  articles.value = data.map((v: IBookArticles, i: number) => {
    return {
      ...v,
      showUnlock: v.is_unlock === 0 && v.price > 0 && i < paySection.value + 2,
      showApp: v.price > 0 && i >= paySection.value + 2,
    }
  })
}

// 查看目录
const openDirectory = () => {
  showDirectory.value = true
  getArticles(true)
}

// 获取文章内容(预览)
const getArticlePreview = async () => {
  const { data } = await asyncGetBookArticlePreview({
    chapter_id: chapterId.value,
  })
  article.value = data
}

// 获取文章内容(解锁)
const unlockArticle = () => {
  asyncGetBookArticle({ chapter_id: chapterId.value, consume_confirm: 0 })
    .then(res => {
      article.value = res.data
      isPreview.value = false
    })
    .catch(err => {
      const status = err.response.data.status || err.response.data.code
      if (status === 90002) {
        isPreview.value = !showToAppModal.value
        getArticlePreview()
      }
    })
}

// 点击解锁按钮
const unlockBtn = () => {
  if (wallet.value < article.value.price) {
    zaq('chapterdetails-torecharge-click', {
      novel_id: bookInfo.value.id,
      chapter_id: chapterId.value,
      chapter_status: 1,
    })
    router.push({
      path: '/c',
      query: {
        channel: 'PC_ChapterDetail_100',
        chapter_id: chapterId.value,
      },
    })
  } else {
    zaq('chapterdetails-unlock-click', {
      novel_id: bookInfo.value.id,
      chapter_id: chapterId.value,
    })
    unlockArticle()
  }
}

// 获取书籍信息
const getBookInfo = async () => {
  const { data } = await asyncGetBookInfo(route.params.novelId as string)
  bookInfo.value = data
  await getArticles()
  chapterId.value = `${route.query.chapter || data.first_chapter_id}`
  console.log('chapterId', chapterId.value)

  let isAutoUnlock = false
  articles.value.forEach((v: IBookArticles) => {
    if (`${v.id}` === chapterId.value) {
      // 是否未解锁
      const isUnlock = v.price > 0 && v.is_unlock === 0 && v.idx < paySection.value + 3
      isPreview.value = isUnlock && !bookStore.isAutoUnlock && !bookInfo.value.is_read_pass
      showToAppModal.value = v.idx >= paySection.value + 3 || (isUnlock && (bookInfo.value.is_read_pass as boolean))
      isAutoUnlock = v.price > 0 && v.is_unlock === 0 && bookStore.isAutoUnlock
    }
  })

  zaq('chapterdetails-pcautounlock-click', { isAutoUnlock })

  // 如果是付费未解锁章节，则获取文章预览内容
  if (isPreview.value || showToAppModal.value) {
    await getArticlePreview()
  } else {
    await unlockArticle()
  }

  // 登录了记录浏览记录
  if (userStore.token) {
    addHistory()
  }
}

// 去登录
const gotoLogin = () => {
  zaq('chapterdetails-login-click', {
    novel_id: bookInfo.value.id,
    chapter_id: chapterId.value,
  })
  userStore.setShowLogin(true)
}

// 选择章节
const selectChapter = (item: IBookArticles) => {
  router.replace(`/bookDetail/${bookInfo.value.id}?chapter=${item.id}`)
  showDirectory.value = false
}

// 上一章
const prevChapter = () => {
  router.replace(`/bookDetail/${bookInfo.value.id}?chapter=${article.value.prev_id}`)
}

// 下一章
const nextChapter = () => {
  router.replace(`/bookDetail/${bookInfo.value.id}?chapter=${article.value.next_id}`)
}

// 是否显示底部工具栏
const { y } = useWindowScroll()
const showBottom = ref(true)
watchDebounced(
  y,
  (val, old) => {
    showBottom.value = val < old
  },
  { debounce: 100, maxWait: 300 },
)

// 主题
const themes: { [v: string]: string }[] = [
  { id: '1', primary: '#F5F5F5', secondary: '#f0f0f0' },
  { id: '2', primary: '#EFE7D1', secondary: '#ECE3CC' },
  { id: '3', primary: '#DBE7E2', secondary: '#D3E1DB' },
  { id: '4', primary: '#131313', secondary: '#0A0A0A' },
]

// 切换主题
const changeTheme = (item: { [v: string]: string }) => {
  appStore.setAppCookieData('readTheme', item)
}

// 修改字体
const changeFont = (type: string) => {
  showBottom.value = true
  const size = parseInt(appStore.readFont)
  if (!size || isNaN(size)) {
    appStore.setAppCookieData('readFont', 16)
    return
  }
  if (type === 'add') {
    if (size === 30) return
    appStore.setAppCookieData('readFont', size + 1)
    return
  }
  if (type === 'sub') {
    if (size === 12) return
    appStore.setAppCookieData('readFont', size - 1)
  }
}

// 加入书架
const shelfSetting = async () => {
  if (bookInfo.value.is_add_library !== 1) {
    await asyncAddBookShelf({ novel_id: bookInfo.value.id })
    zaq('chapterdetails-addbookshelf-click', { novel_id: bookInfo.value.id })
    message.success(t('addedToLibrary'))
  }
  await getBookInfo()
}

// 监听路由变化获取数据
watch(
  route,
  async val => {
    if (val.params.novelId) {
      getBookInfo()
      if (userStore.token) {
        getUserBalance()
      }
    }
  },
  { immediate: true },
)

// 是否是黑暗主题
const isDark = computed(() => appStore.readTheme.id === '4')

// 排序
const isReverse = ref(false)
const reverseFun = () => {
  isReverse.value = !isReverse.value
  articles.value = articles.value.reverse()
}

onBeforeUnmount(() => {
  zaq('chapterdetails-fontsize-view', {
    novel_id: bookInfo.value.id,
    font_size: appStore.readFont,
  })
  zaq('chapterdetails-background-click', {
    novel_id: bookInfo.value.id,
    theme: JSON.stringify(appStore?.readTheme),
  })
})
</script>

<template>
  <div>
    <div web-container flex flex-col items-center pt-50px>
      <template v-if="article.idx === 1">
        <div v-if="bookInfo.avatar" w-195px h-300px relative>
          <img
            h-222px
            absolute
            bottom-38px
            left-0
            right-0
            m-auto
            filter-blur-20px
            opacity-80
            rounded-lg
            :src="bookInfo.avatar"
            alt="avator"
          />
          <img w-full h-260px absolute left-0 top-0 rounded-lg :src="bookInfo.avatar" alt="avator" />
        </div>

        <h1 text-24px font-medium :class="{ 'text-white': isDark }">
          {{ bookInfo.title }}
        </h1>
        <div v-if="bookInfo.author" flex items-center overflow-hidden mt-18px>
          <img
            w-16px
            h-16px
            mr-8px
            rounded-full
            objectFit="cover"
            :src="bookInfo.author_avatar || localImage('avator-icon.svg')"
            :style="`fill: drop-shadow(${isDark ? '20px 0 #32bb65' : ''});`"
            alt=""
          />
          <span line-clamp-1 :class="isDark ? 'text-[#737373]' : 'text-[#8C8C8C]'">
            {{ bookInfo.author }}
          </span>
        </div>
        <div my-20px w-full>
          <NDivider titlePlacement="center">
            <iconpark-icon
              width="18"
              height="18"
              name="zhangjiefengetubiao"
              :color="localLang === 'ar' ? '#fd6b0a' : '#DE0008'"
            />
          </NDivider>
        </div>
      </template>

      <h1 w-full text-32px font-medium :class="{ 'text-[#D9D9D9]': isDark }">
        {{ article.title }}
      </h1>
      <section mt-25px mb-64px relative w-full :class="{ 'h-88px overflow-hidden': showToAppModal || isPreview }">
        <div
          w-full
          whitespace-pre-wrap
          break-words
          :class="`
            ${isDark ? 'text-[#a6a6a6]' : 'text-[#595959]'}
          `"
          :style="{ fontSize: `${appStore.readFont || 16}px`, lineHeight: 1.4 }"
          v-html="article.content"
        />
        <div v-if="showToAppModal || isPreview" absolute top-0 left-0 w-full h-full class="bg-gradient" />
      </section>

      <div v-if="showToAppModal || isPreview" flex flex-col items-center w-444px>
        <img h-24px mb-40px :src="localImage(`book/divider${isDark ? '-dark' : ''}.png`)" alt="" />

        <!-- 限时优惠 -->
        <div
          v-if="bookInfo.discount_promotion?.discount"
          class="min-w-429px gap-4 mb-6 h-33px rounded-4px flex items-center justify-between rounded-8px h-65px px-15px"
          :class="isDark ? 'bg-countdown-dark' : 'bg-countdown'"
        >
          <div flex items-center>
            <img w-32px h-38px mb-2 :src="localImage('countdown-fire.png')" alt="" />
            <div fw-600 text-primary ml-2>
              {{
                t('limitedTimeOffer', {
                  x: bookInfo.discount_promotion?.discount,
                })
              }}
            </div>
          </div>
          <Countdown
            title=""
            :start-time="bookInfo.discount_promotion!.start_time_ts || 0"
            :end-time="bookInfo.discount_promotion!.end_time_ts || 0"
          />
        </div>

        <!-- 登录 -->
        <NButton v-if="!userStore.token" w-150px mb-80px class="btn" @click="gotoLogin">
          {{ t('signIn') }}
        </NButton>

        <!-- 前往App下载 -->
        <div v-else-if="showToAppModal" w-444px flex flex-col items-center>
          <h1 text-24px font-semibold leading-35px :class="{ 'text-[#d9d9d9]': isDark }">
            {{ t('gotoTaponApp') }}
          </h1>
          <div text-16px mt-8px mb-32px text-center :class="isDark ? 'text-[#737373]' : 'text-[#8C8C8C]'">
            {{ t('continueReadMore') }}
          </div>
          <div mb-4 flex gap-15px rounded-lg overflow-hidden>
            <img
              flex-none
              rounded-lg
              w-96px
              h-96px
              :src="localImage(`${localLang === 'ar' ? 'ewm-ar.png' : 'ewm.png'}`)"
              alt=""
            />
            <div flex-none>
              <a :href="APP_STORE_LINK" target="_blank">
                <img w-134px :src="localImage('app-store.png')" alt="" />
              </a>
              <a :href="GOOGLE_STORE_LINK" target="_blank">
                <img w-134px mt-16px :src="localImage('google-play.png')" alt="" />
              </a>
            </div>
          </div>
          <div w-full h-65px relative mt-30px mb-80px>
            <img
              absolute
              top-0
              left-0
              w-full
              :src="localImage(`${localLang === 'ar' ? 'book/to-app-banner-ar.png' : 'book/to-app-banner.png'}`)"
              alt=""
            />
            <div h-full absolute top-0 right-16px flex justify-between items-center w="80%" h="90%">
              <div font-semibold text-16px mr-24px color="#FF6F33">
                {{ t('moreBonusTip') }}
              </div>
              <NButton
                tag="a"
                :href="`/c?channel=PC_ChapterDetail_100&chapter_id=${chapterId}`"
                rounded-lg
                color="#fff"
                textColor="text-primary"
                @click="
                  zaq('chapterdetails-torecharge-click', {
                    novel_id: bookInfo.id,
                    chapter_id: chapterId,
                    chapter_status: 1,
                  })
                "
              >
                {{ t('topUpBtn') }}
              </NButton>
            </div>
          </div>
        </div>

        <!-- 预览，未解锁 -->
        <div v-else-if="isPreview" mb-80px flex flex-col items-center>
          <i18n-t keypath="coinsUnlockThisChapter" tag="h3" text-16px :class="{ 'text-white': isDark }">
            <template #x>
              <span text-24px text-primary>{{ article.price }}</span>
              <span
                v-if="article.original_price && article.original_price > article.price"
                text="#737373 16px"
                leading-22px
                px-1
                line-through
              >
                {{ article.original_price }}
              </span>
            </template>
          </i18n-t>
          <NButton px-48px h-40px my-16px text-18px class="btn" @click="unlockBtn">
            {{ wallet >= article.price ? t('unlockNow') : t('getMoreCoins') }}
          </NButton>
          <div
            flex
            items-center
            select-none
            cursor-pointer
            text-14px
            color="#8C8C8C"
            @click="bookStore.setIsAutoUnlock(!bookStore.isAutoUnlock)"
          >
            <div i-carbon-checkmark-outline :class="{ 'text-primary': bookStore.isAutoUnlock }" />
            <span pl-6px>{{ t('autoUnlock') }}</span>
          </div>
        </div>
      </div>

      <!-- 上一章和下一章 -->
      <div v-if="!appStore.isLoading" flex justify-center gap-80px>
        <button
          rounded-lg
          flex
          p-8px
          pr-32px
          :class="isDark ? 'bg-[rgba(255,255,255,0.08)] text-[#A6A6A6]' : 'bg-[rgba(0,0,0,0.0800)] text-[#595959]'"
          :disabled="article.idx === 1 || article.prev_id === 0"
          :opacity="article.idx === 1 || article.prev_id === 0 ? '50' : '100'"
          @click="prevChapter"
        >
          <iconpark-icon width="22" height="22" name="zuojiantou" />
          <span pl-6px>{{ t('lastChapter') }}</span>
        </button>
        <button
          rounded-lg
          flex
          p-8px
          pl-32px
          :class="isDark ? 'bg-[rgba(255,255,255,0.08)] text-[#A6A6A6]' : 'bg-[rgba(0,0,0,0.0800)] text-[#595959]'"
          :disabled="article.next_id === 0"
          :opacity="article.next_id === 0 ? '50' : '100'"
          @click="nextChapter"
        >
          <span pr-6px>{{ t('nextChapter') }}</span>
          <iconpark-icon width="22" height="22" name="youjiantou" />
        </button>
      </div>
    </div>

    <!-- 底部悬浮功能 -->
    <div
      w-full
      fixed
      z-3000
      left-0
      bottom-0
      transition="height 0.3s"
      :class="`
        ${showBottom ? 'h-70px' : 'h-0'}
        ${isDark ? 'bg-[#2E2E2E] box-shadow-1' : 'bg-white box-shadow'}
      `"
    >
      <div web-container h-full flex items-center justify-between>
        <div flex-1 flex gap-40px items-center h-full>
          <!-- 目录 -->
          <iconpark-icon
            v-if="!showDirectory"
            width="22"
            height="22"
            cursor-pointer
            name="mulu"
            :color="isDark ? '#d9d9d9' : '#262626'"
            @click="openDirectory"
          />
          <iconpark-icon
            v-else
            name="guanbi"
            width="22"
            height="22"
            cursor-pointer
            :color="isDark ? '#d9d9d9' : '#262626'"
          />
          <!-- 设置 -->
          <NPopover
            trigger="click"
            :showArrow="false"
            arrow-wrapper-class="addPopverClass"
            displayDirective="show"
            raw
            rounded-lg
          >
            <template #trigger>
              <div flex items-center h="3/4" @click="zaq('chapterdetails-pcsetup-click', { novel_id: bookInfo.id })">
                <iconpark-icon
                  width="22"
                  height="22"
                  name="shezhi"
                  cursor-pointer
                  :color="isDark ? '#d9d9d9' : '#262626'"
                />
              </div>
            </template>
            <div p-24px rounded-lg :class="isDark ? 'bg-[#2E2E2E] text-[#d9d9d9]' : 'bg-white'">
              <h4 text-16px mb-2>{{ t('background') }}</h4>
              <div flex gap-16px items-center justify-between mb-24px>
                <div
                  v-for="item in themes"
                  :key="item.id"
                  w-50px
                  h-40px
                  rounded-lg
                  cursor-pointer
                  :style="{ backgroundColor: item.primary }"
                  :class="[
                    {
                      'border border-primary': item.primary === appStore.readTheme.primary,
                    },
                  ]"
                  @click="changeTheme(item)"
                />
              </div>
              <h4 text-16px mb-2>{{ t('fontSize') }}</h4>
              <div h-40px flex text-16px rounded-lg overflow-hidden :class="isDark ? 'bg-[#404040]' : 'bg-[#F5F5F5]'">
                <button
                  flex-1
                  flex
                  justify-center
                  items-center
                  :class="`
                    ${isDark ? 'bg-[#404040] text-[#d9d9d9]' : 'bg-[#F5F5F5]'}
                    ${+appStore.readFont <= 12 ? 'opacity-50' : ''}
                  `"
                  :disabled="+appStore.readFont <= 12"
                  @click="changeFont('sub')"
                >
                  <iconpark-icon
                    width="22"
                    height="22"
                    :name="localLang === 'ar' ? 'zihaojian-ar' : 'zihaojia'"
                    :color="isDark ? '#d9d9d9' : '#262626'"
                  />
                </button>
                <div
                  flex-1
                  flex
                  justify-center
                  items-center
                  rounded-lg
                  my-1px
                  :class="isDark ? 'bg-[#737373]' : 'bg-white'"
                >
                  {{ appStore.readFont }}
                </div>
                <button
                  flex-1
                  flex
                  justify-center
                  items-center
                  :disabled="+appStore.readFont >= 30"
                  :class="`
                    ${isDark ? 'bg-[#404040] text-[#d9d9d9]' : 'bg-[#F5F5F5]'}
                    ${+appStore.readFont >= 30 ? 'opacity-50' : ''}
                  `"
                  @click="changeFont('add')"
                >
                  <iconpark-icon
                    width="22"
                    height="22"
                    :name="localLang === 'ar' ? 'zihaojia-ar' : 'zihaojia'"
                    :color="isDark ? '#d9d9d9' : '#262626'"
                  />
                </button>
              </div>
              <div
                flex
                items-center
                my-24px
                select-none
                cursor-pointer
                :class="isDark ? 'text-[#737373]' : 'text-[#8C8C8C]'"
                @click="bookStore.setIsAutoUnlock(!bookStore.isAutoUnlock)"
              >
                <div i-carbon-checkmark-outline :class="{ 'text-primary': bookStore.isAutoUnlock }" />
                <span pl-6px>{{ t('autoUnlock') }}</span>
              </div>
            </div>
          </NPopover>
        </div>
        <div v-if="bookInfo.is_add_library !== 1" flex-1 flex items-center justify-center cursor-pointer>
          <div flex gap-8px items-center justify-center @click="shelfSetting">
            <img w-22px h-22px :src="localImage(`book/add-icon${localLang === 'ar' ? '-ar' : ''}.png`)" alt="" />
            <span text-primary text-16px>{{ t('addToLibrary') }}</span>
          </div>
        </div>
        <div flex-1 flex items-center justify-end text-22px :color="isDark ? '#a6a6a6' : '#595959'">
          <button
            flex
            items-center
            cursor-pointer
            bg-transparent
            :opacity="article.idx === 1 || article.prev_id === 0 ? '50' : '100'"
            :disabled="article.idx === 1 || article.prev_id === 0"
            @click="prevChapter"
          >
            <iconpark-icon width="22" height="22" name="zuojiantou" :color="isDark ? '#a6a6a6' : '#595959'" />
            <span pr-20px pl-6px text-16px>{{ t('lastChapter') }}</span>
          </button>
          <button
            flex
            items-center
            cursor-pointer
            bg-transparent
            :opacity="article.next_id === 0 ? '50' : '100'"
            :disabled="article.next_id === 0"
            @click="nextChapter"
          >
            <span pl-20px pr-6px text-16px>{{ t('nextChapter') }}</span>
            <iconpark-icon width="22" height="22" name="youjiantou" :color="isDark ? '#a6a6a6' : '#595959'" />
          </button>
        </div>
      </div>
    </div>

    <!-- 目录 -->
    <NDrawer
      v-model:show="showDirectory"
      :width="360"
      placement="left"
      :show-mask="false"
      display-directive="show"
      :style="`${isDark ? 'background: #2E2E2E' : ''}`"
      select-none
    >
      <NDrawerContent>
        <template #header>
          <div
            flex
            justify-between
            items-center
            w-full
            py-16px
            px-24px
            :class="isDark ? 'bg-[#242424] text-[#D9D9D9]' : 'bg-[#FAFAFA]'"
          >
            <h2>{{ t('list') }}</h2>
            <iconpark-icon
              width="24"
              height="24"
              cursor-pointer
              :name="
                !isReverse ? (localLang === 'ar' ? 'zhengxu-ar' : 'zhengxu') : localLang === 'ar' ? 'daoxu-ar' : 'daoxu'
              "
              @click="reverseFun"
            />
          </div>
        </template>
        <div
          overflow-y-auto
          py-6
          :class="`h-9/10 ${isDark ? 'bg-[#2E2E2E] text-[#A6A6A6] dark-scroll' : 'text-[#595959]'}`"
        >
          <template v-if="!drawerLoading">
            <div
              v-for="item in articles"
              :key="item.id"
              w-full
              h-46px
              px-24px
              flex
              justify-between
              items-center
              cursor-pointer
              hover:color-primary
              :class="item.id === article.id ? 'text-primary' : ''"
              @click="selectChapter(item)"
            >
              <h3 line-clamp-1 class="w-4/5">{{ item.title }}</h3>
              <iconpark-icon
                v-if="item.showUnlock && !bookInfo.is_read_pass"
                width="24"
                height="24"
                name="suo"
                :color="isDark ? '#595959' : '#BFBFBF'"
              />
              <iconpark-icon
                v-if="item.showApp || (item.showUnlock && bookInfo.is_read_pass)"
                width="24"
                height="24"
                name="app"
                :color="isDark ? '#595959' : '#BFBFBF'"
              />
            </div>
          </template>
          <div v-else w-full h-full flex justify-center items-center>
            <NSpin size="medium" />
          </div>
        </div>
      </NDrawerContent>
    </NDrawer>
  </div>
</template>

<style lang="less" scoped>
:deep(.n-divider__line) {
  background-color: #fac4ba;
  --n-color: #fac4ba;
}

:deep(.n-divider.n-divider--vertical) {
  --n-color: #8c8c8c !important;
  height: 32px;
}

.box-shadow {
  box-shadow: 0px 0px 20px 1px rgba(227, 227, 227, 0.6);
}

.box-shadow-1 {
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.6);
}

.bg-gradient {
  background: linear-gradient(180deg, rgba(245, 245, 245, 0) 0%, v-bind('appStore.readTheme.primary') 100%);
}

// 目录
:deep(.n-drawer-header) {
  --n-header-padding: 0 !important;
  --n-header-border-bottom: none;
}

:deep(.n-drawer-body-content-wrapper) {
  --n-body-padding: 0 !important;
}

:deep(.n-drawer-header__main) {
  width: 100% !important;
}

.dark-scroll {
  &::-webkit-scrollbar-thumb {
    background-color: #242424;
  }

  &::-webkit-scrollbar-track {
    background-color: #404040;
  }
}

.bg-countdown {
  background: linear-gradient(90deg, #f6ebd5 0%, #ffddd8 100%);

  &-dark {
    background: linear-gradient(90deg, rgba(255, 237, 220, 0.1) 0%, rgba(255, 221, 216, 0.1) 100%);
  }
}
</style>
